import React, { useState } from 'react';
import theme from './BudgetTable.module.scss';
import { BudgetItemDto, BudgetGroupType, BudgetItemSource } from '@/types';
import { Trans } from '@lingui/react/macro';
import MultiList from '@/components/MultiList';
import budgetListItemTheme from '../BudgetList.module.scss';
import { DateDisplay } from '@/components/localization';
import Tags from '@/components/Tags';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLockOpen, faLock, faEdit, faFileContract } from '@fortawesome/pro-light-svg-icons';
import InfoIconWithTooltip from '@/seller/components/InfoIconWithTooltip';
import { classnames } from '@/utils/html';
import { useHistory } from 'react-router';
import { getDisplayText } from '../..';
import { getProjectCatalogProductFileThumbnailUrl } from '@/services/project-catalog';
import BudgetItemPrice from '../BudgetItemPrice';
import { Thumbnail } from '@ygtechnologies/bygr-frontend-component-library';

interface BudgetTableProps {
    data: BudgetItemDto[];
    type: BudgetGroupType;
}

const BudgetTable: React.FC<BudgetTableProps> = ({ data, type }) => {
    const history = useHistory();

    const open = (row: BudgetItemDto) => {
        if (type === BudgetGroupType.ChangeRequests) {
            history.push(`/change-requests/${row.changeRequestId}`);
        } else if (type === BudgetGroupType.Normal) {
            history.push(row.roomTagId ? `/options/room/${row.roomTagId}` : '/options');
        }
    };

    const isClickable = (row: BudgetItemDto) => {
        if (type === BudgetGroupType.ChangeRequests) return true;
        if (type === BudgetGroupType.Normal) return true;
        return false;
    };

    const getThumbnail = (budgetItem: BudgetItemDto) => {
        if (budgetItem.isNone) {
            return null;
        }

        if (budgetItem.source === BudgetItemSource.Option) {
            if (!budgetItem.selectedOptionImages || budgetItem.selectedOptionImages.length === 0) {
                return null;
            }

            return <Thumbnail src={getProjectCatalogProductFileThumbnailUrl(budgetItem.selectedOptionImages[0].id)} />;
        }

        return <FontAwesomeIcon className={theme.icon} icon={faFileContract} size="2x" />;
    };

    return (
        <>
            <MultiList hideHeaders className={theme.table} pageSize={0} type="table" data={data} onRowClick={open}>
                <MultiList.Column
                    width="45%"
                    header={<Trans>Option</Trans>}
                    keyName="displayName"
                    renderFunction={(v, row: BudgetItemDto) => (
                        <div className={classnames(theme.option, { [theme.clickable]: isClickable(row) })}>
                            <div className={budgetListItemTheme.image}>{getThumbnail(row)}</div>
                            <div className={theme.optionText}>
                                {row.buyerOptionGroupName && <small>{row.buyerOptionGroupName}</small>}
                                <span>
                                    {getDisplayText(row)}
                                    {/* {row.documentTypeId && documentTypes && ` - ${documentTypes[row.documentTypeId]}`} */}
                                    {row.canChangeSelection && <FontAwesomeIcon icon={faEdit} />}
                                </span>
                                {row.description && <small className={theme.description}>{row.description}</small>}
                                {row.tags && row.tags.length > 0 && <Tags size="small" tags={row.tags} />}
                            </div>
                        </div>
                    )}
                />
                {type === BudgetGroupType.Normal ? (
                    <MultiList.Column
                        width="45%"
                        header={
                            <span className={theme.deadlineHeader}>
                                <Trans>Deadline</Trans>
                            </span>
                        }
                        keyName={'deadline'}
                        renderFunction={(v, r: BudgetItemDto) => (
                            <div className={classnames(theme.deadline, { [theme.clickable]: isClickable(r) })}>
                                <FontAwesomeIcon icon={r.canChangeSelection ? faLockOpen : faLock} />
                                <DateDisplay value={v} />
                                {r.canChangeSelection && (
                                    <InfoIconWithTooltip
                                        color="light"
                                        title={<Trans>Deadline - "{r.deadlineName}"</Trans>}
                                        content={
                                            <Trans>
                                                The deadline for {r.displayName} has not yet passed, but will expire on
                                                <br />
                                                <DateDisplay value={v} />
                                            </Trans>
                                        }
                                    />
                                )}
                            </div>
                        )}
                    />
                ) : (
                    <MultiList.Column keyName="deadline" header="" width="45%" />
                )}
                <MultiList.Column
                    header={<Trans>Price</Trans>}
                    keyName="totalPrice"
                    renderFunction={(v, r: BudgetItemDto) => (
                        <BudgetItemPrice
                            budgetItem={r}
                            className={classnames(theme.price, { [theme.clickable]: isClickable(r) })}
                        />
                    )}
                />
            </MultiList>
        </>
    );
};

export default BudgetTable;
